/**
 * Phase 336 wave 2 task 2.2 (#1163) — the Sales Activity PAGE MODEL.
 *
 * <p>Every piece of presentation arithmetic the page needs lives here, and nothing else
 * does: no fetch, no React, no clock. The page (task 3.1) reads the wire through
 * `useWeeklyActivity` / `useRoster`, hands the cells and the roster to these functions, and
 * renders what comes back. That split is not tidiness — it is what makes the rules below
 * testable as rules rather than as pixels, and it is why the table (newest-first) and the
 * trend charts (oldest-first) can never disagree about which weeks exist: they are two
 * projections of ONE axis computed in one place.</p>
 *
 * <h3>The four rules this module exists to enforce</h3>
 * <ol>
 *   <li><b>One metric vocabulary.</b> {@link SALES_ACTIVITY_METRICS} is the single table of
 *       (wire key → full label → column abbreviation), in the locked display order with
 *       Notes LAST. Tiles and trend titles take the full label; the table's column heads
 *       take the abbreviation and pay for it with {@link METRIC_LEGEND_LINE}. The same nine
 *       keys, the same order and the same labels as the Teams weekly card
 *       (`WeeklyReportChart.ChartedMetrics` + `WeeklyReportTrendRows.MetricTokens`) —
 *       pinned against those C# tables in the tests, because a client reading the Monday
 *       card and this page on Tuesday must be reading the same words for the same numbers.
 *       "Forwarding Interviews" therefore stays unabbreviated wherever a full label is
 *       shown (the locked-vocabulary call, PLAN 334 § Edge cases).</li>
 *   <li><b>Month bands come from the Thursday rule.</b> {@link isoWeekToMonth} is the web
 *       twin of `ReportingReadService.IsoWeekToYearMonth` (ISO 8601 §4.3.2: the week belongs
 *       to the month containing its Thursday). Deriving a band any other way — the week's
 *       Monday, say — would put `2026-W01` under December while every fact the engine
 *       months says January, and a portal band that disagrees with a fact month is a bug
 *       nobody can debug from a screenshot.</li>
 *   <li><b>A gap is absence; a zero is data.</b> An ISO week with NO consolidated cells,
 *       between the tenant's first and last observed week, is a GAP: a labelled row in the
 *       table ({@link WEEK_GAP_LABEL}) and a `null` point — a broken line — in every trend
 *       series. A week that IS recorded and happens to be zero is DATA: a muted dash in the
 *       cell and a plotted 0 on the line. The W07 class (#1056) is the live example, and
 *       the page rendering it as a labelled gap IS the page working.</li>
 *   <li><b>Identity comes from the shared primitive.</b> Roster order, the `User {id}`
 *       fallback and the support-consultant flag are read through
 *       `@ardept/design-system`'s `orderRoster` / `displayName` — the same module the
 *       charts colour from — so a consultant keeps one position, one name and one hue
 *       across every surface. This module never re-implements that ordering.</li>
 * </ol>
 *
 * <p>All exports are pure functions of their arguments. `now` is a PARAMETER of
 * {@link ytdTiles} with no default: a module that reads the clock cannot be tested at a
 * year boundary, which is the one moment the YTD tiles are hard to get right.</p>
 */
import { orderRoster, displayName } from "@ardept/design-system";
import type { ReportingRosterConsultant, WeeklyActivityCell } from "../api/types.js";

// ── The locked metric vocabulary ────────────────────────────────────────────────────────

/**
 * THE table. Nine rows, in the locked DISPLAY order — the eight typed metrics in their spec
 * order, then Notes LAST.
 *
 * <p>Note that this is not the order the engine lists its keys in
 * (`ReportingReadService.LockedMetricVocabulary` is storage order, alphabetical-ish by
 * accident); it is the order the family PRESENTS them in, pinned by
 * `WeeklyReportChart.ChartedMetrics` and `WeeklyReportTrendRows.MetricTokens`. The
 * two sets are identical and the tests assert it against the C# source, so a tenth metric
 * or a renamed key fails here rather than silently vanishing from the page.</p>
 *
 * <p>Notes is last, and not merely by convention: it is the breadth counter — every note
 * logged, so ≥ every typed bucket by construction (`WeeklyReportChart.cs:61-63`) — which is
 * also why {@link trendSeries} puts it on its own scale.</p>
 */
const METRIC_TABLE = [
  { key: "new_contacts", label: "New Contacts", abbrev: "NC" },
  { key: "new_vacancies", label: "New Vacancies", abbrev: "NV" },
  { key: "submissions", label: "Submissions", abbrev: "Sub" },
  { key: "interviews", label: "Interviews", abbrev: "Int" },
  { key: "forwarding_interviews", label: "Forwarding Interviews", abbrev: "FI" },
  { key: "cltvs", label: "CLTVs", abbrev: "CLTV" },
  { key: "cints", label: "CINTs", abbrev: "CINT" },
  { key: "spec_cvs", label: "Spec CVs", abbrev: "Spec" },
  { key: "notes", label: "Notes", abbrev: "Notes" },
] as const;

/** One of the nine locked `metric_key` values, as the wire spells them. */
export type MetricKey = (typeof METRIC_TABLE)[number]["key"];

/** One row of the metric vocabulary: the wire key, the full label, the column abbreviation. */
export interface SalesActivityMetric {
  /** The `metric_key` on the wire (snake_case). */
  key: MetricKey;
  /** The full name — YTD tiles, trend chart titles, split-row headings. */
  label: string;
  /** The short column head for the weekly table. Expanded by {@link METRIC_LEGEND_LINE}. */
  abbrev: string;
}

/** The nine metrics in locked display order, Notes LAST. See {@link METRIC_TABLE}. */
export const SALES_ACTIVITY_METRICS: readonly SalesActivityMetric[] = METRIC_TABLE;

const METRIC_KEYS: ReadonlySet<string> = new Set(METRIC_TABLE.map((m) => m.key));

/** True when a wire `metric_key` is one this page renders. */
export function isMetricKey(key: string): key is MetricKey {
  return METRIC_KEYS.has(key);
}

/**
 * The muted dash a zero renders as — U+2013 EN DASH, the same glyph the Teams card's
 * `WeeklyReportTrendRows.ZeroText` uses. The test pins the CODE POINT, not the
 * appearance: a hyphen-minus pasted in here looks almost identical and would quietly
 * de-align this page from the Monday card.
 */
export const ZERO_DASH = "–";

/** The label a gap week carries in the weekly table. Absence, said out loud. */
export const WEEK_GAP_LABEL = "no data recorded";

/**
 * Phase 380 wave 3 task 3.3 (#1329) — the words a week whose harvest ran short wears, so its
 * numbers read as FLOORS rather than as final figures.
 *
 * <p>The SAME sentence the Monday Teams card discloses with
 * (`WeeklyReportCardBody.PartialDataWarningPrefix`), minus that card's leading ⚠️ glyph — the
 * page draws its own mark, in its own type. Pinned against the C# constant in the tests, for
 * the reason {@link ZERO_DASH} is: a client who reads the card on Monday and this page on
 * Tuesday must be reading ONE disclosure, not two paraphrases of it. Neither surface re-derives
 * "is this short?" — both read the one upstream fact.</p>
 *
 * <p>It lives beside {@link WEEK_GAP_LABEL} rather than in the page because this module is
 * where the family's vocabulary lives, and because a second copy of the sentence in a `.tsx`
 * is exactly how the two surfaces start disagreeing. Consumed by
 * {@link WeekRow.activityHarvestIncomplete}'s renderer.</p>
 */
export const PARTIAL_DATA_LABEL = "Partial data — harvest incomplete";

/** The badge {@link trendSeries} puts on the one series that is NOT on the shared scale. */
export const OWN_SCALE_LABEL = "own scale";

/** The metric that gets its own y-scale: the breadth counter (see {@link METRIC_TABLE}). */
export const OWN_SCALE_METRIC: MetricKey = "notes";

/**
 * The footnote that pays for the abbreviated column heads: every abbreviation expanded, plus
 * the zero-dash rule named.
 *
 * <p>DERIVED from {@link METRIC_TABLE} rather than typed out, so a vocabulary change cannot
 * leave a stale legend behind. Metrics whose abbreviation IS their label (Notes) are skipped
 * — "Notes Notes" reads as a typo, not as help. The wording mirrors
 * `WeeklyReportTrendRows.BuildLegend()` minus its week-on-week glyphs, which belong to
 * the Teams card's per-consultant trend rows and have no counterpart in this table.</p>
 */
export const METRIC_LEGEND_LINE: string =
  METRIC_TABLE.filter((m) => m.abbrev !== m.label)
    .map((m) => `${m.abbrev} ${m.label}`)
    .join(" · ") + ` — dash (${ZERO_DASH}) = zero`;

// ── Value formatting ────────────────────────────────────────────────────────────────────

const NUMBER_FORMAT = new Intl.NumberFormat("en-GB");

/**
 * The family zero rule: a zero reads as a muted dash, anything else as a grouped number.
 *
 * <p>"Nothing happened" should read as absence rather than as a result competing for
 * attention with the numbers that matter — the same rule the Teams card and the interim
 * portal page already apply, so a client sees one convention everywhere.</p>
 */
export function formatMetricValue(value: number): string {
  return value === 0 ? ZERO_DASH : NUMBER_FORMAT.format(value);
}

/** One rendered cell: the metric it belongs to, its number, and how that number reads. */
export interface MetricValue {
  key: MetricKey;
  /** Full label — for tiles, headings and `aria-label`s. */
  label: string;
  /** Column-head abbreviation — for the table. */
  abbrev: string;
  value: number;
  /** {@link formatMetricValue} of `value`: the dash, or the grouped number. */
  display: string;
  /** True when the value is zero — the page's hook for the muted treatment. */
  isZero: boolean;
}

type MetricTotals = Record<MetricKey, number>;

function zeroTotals(): MetricTotals {
  const totals = {} as MetricTotals;
  for (const metric of METRIC_TABLE) totals[metric.key] = 0;
  return totals;
}

/** Project a totals record into the nine display cells, in locked display order. */
function metricValues(totals: MetricTotals): MetricValue[] {
  return METRIC_TABLE.map((metric) => {
    const value = totals[metric.key];
    return {
      key: metric.key,
      label: metric.label,
      abbrev: metric.abbrev,
      value,
      display: formatMetricValue(value),
      isZero: value === 0,
    };
  });
}

// ── ISO week arithmetic (the Thursday rule) ─────────────────────────────────────────────

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

/** The engine writes `{isoYear:D4}-W{isoWeekNum:D2}` (`ReportingFactStore.DeriveIsoWeek`). */
const ISO_WEEK_RE = /^(\d{4})-W(\d{2})$/;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

interface IsoWeekParts {
  /** The ISO week-NUMBERING year, which is not always the calendar year of the week's days. */
  year: number;
  /** 1..53. */
  week: number;
}

function parseIsoWeek(label: string): IsoWeekParts | null {
  const match = ISO_WEEK_RE.exec(label);
  if (match === null) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (week < 1 || week > 53) return null;
  return { year, week };
}

function formatIsoWeek(parts: IsoWeekParts): string {
  return `${String(parts.year).padStart(4, "0")}-W${String(parts.week).padStart(2, "0")}`;
}

/**
 * The UTC midnight of an ISO week's Monday.
 *
 * <p>All week arithmetic in this module runs on UTC milliseconds. A local-time step would
 * gain or lose an hour across a DST boundary, and 25 hours later a `+7 days` step lands on a
 * Sunday — which would silently renumber every week after the clocks change.</p>
 */
function isoWeekMondayUtc(parts: IsoWeekParts): number {
  // ISO 8601: week 1 is the week containing 4 January.
  const jan4 = Date.UTC(parts.year, 0, 4);
  const jan4Dow = new Date(jan4).getUTCDay(); // 0 = Sunday
  const jan4IsoDow = jan4Dow === 0 ? 7 : jan4Dow; // 1 = Monday … 7 = Sunday
  const week1Monday = jan4 - (jan4IsoDow - 1) * DAY_MS;
  return week1Monday + (parts.week - 1) * WEEK_MS;
}

/** The ISO week a UTC instant falls in — the inverse of {@link isoWeekMondayUtc}. */
function isoWeekOfUtc(utcMs: number): IsoWeekParts {
  const isoDowIndex = (new Date(utcMs).getUTCDay() + 6) % 7; // 0 = Monday
  // The week's Thursday decides both the year and the week number (ISO 8601 §4.3.2).
  const thursday = utcMs + (3 - isoDowIndex) * DAY_MS;
  const year = new Date(thursday).getUTCFullYear();
  const jan1 = Date.UTC(year, 0, 1);
  const week = Math.floor((thursday - jan1) / WEEK_MS) + 1;
  return { year, week };
}

/**
 * `2026-W01` → `2026-01`: the month band a week belongs to, by the ISO 8601 Thursday rule.
 *
 * <p>The web twin of `ReportingReadService.IsoWeekToYearMonth` (which the read layer uses to
 * month its forecast facts), down to the fallback: an unparseable label is returned
 * UNCHANGED rather than guessed at, so a malformed week groups under itself and is visible
 * instead of being folded into a real month.</p>
 */
export function isoWeekToMonth(isoWeek: string): string {
  const parts = parseIsoWeek(isoWeek);
  if (parts === null) return isoWeek;
  const thursday = new Date(isoWeekMondayUtc(parts) + 3 * DAY_MS);
  const month = String(thursday.getUTCMonth() + 1).padStart(2, "0");
  return `${thursday.getUTCFullYear()}-${month}`;
}

/**
 * `2026-01` → `January 2026`, the band heading.
 *
 * <p>Built from a fixed month table rather than `toLocaleDateString`, for two reasons: the
 * band must read identically on every machine (a locale-dependent heading over
 * locale-independent facts is a support ticket), and constructing a `Date` from a
 * `yyyy-MM` string invites the timezone off-by-one this module spent {@link isoWeekMondayUtc}
 * avoiding. An unrecognised input is returned unchanged, matching
 * {@link isoWeekToMonth}'s fallback.</p>
 */
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return month;
  const name = MONTH_NAMES[Number(match[2]) - 1];
  if (name === undefined) return month;
  return `${name} ${match[1]}`;
}

/**
 * The ISO week-numbering YEAR of a calendar day — what "this year" means for YTD.
 *
 * <p>Read from the LOCAL calendar fields of `now` (the date on the reader's clock), then
 * evaluated in UTC. Reading `now`'s UTC date instead would tell a client an hour ahead of
 * UTC that the new year had not started yet.</p>
 *
 * <p>It is the ISO year, not `getFullYear()`, because the weeks it selects are ISO weeks:
 * on 1 January 2027 the current ISO week is still `2026-W53`, and a YTD tile that dropped it
 * would show a client zeros for a week they had just worked.</p>
 */
export function currentIsoYear(now: Date): number {
  const localMidnightAsUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return isoWeekOfUtc(localMidnightAsUtc).year;
}

// ── The week axis: the one place "which weeks exist" is decided ─────────────────────────

interface AxisWeek {
  isoWeek: string;
  /** True when the fact store holds consolidated cells for this week. False = a GAP. */
  hasData: boolean;
  /** True when THIS week's harvest was known short — see {@link incompleteWeeks}. */
  harvestIncomplete: boolean;
  totals: MetricTotals;
}

/**
 * Distinct ISO weeks across ALL cells, chronologically.
 *
 * <p>Both scopes count towards the axis ENDPOINTS. The engine writes the consolidated row
 * and the per-consultant rows for a week together, so the two sets coincide in practice; if
 * they ever did not, a week with a consultant split but no tenant total should still appear
 * as a row (labelled honestly as having no recorded total) rather than disappear from the
 * page along with its split.</p>
 *
 * <p>Lexical order IS chronological order for these labels — the engine zero-pads the week
 * number (`ReportingFactStore.DeriveIsoWeek`), so `2026-W09` &lt; `2026-W10` as strings.
 * No date parsing, and no ambiguity at a year boundary.</p>
 */
function distinctWeeks(cells: readonly WeeklyActivityCell[]): string[] {
  const weeks = new Set<string>();
  for (const cell of cells) {
    if (cell.iso_week) weeks.add(cell.iso_week);
  }
  return [...weeks].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** Consolidated-scope totals per ISO week, zero-filled across the locked vocabulary. */
function consolidatedTotalsByWeek(
  cells: readonly WeeklyActivityCell[],
): Map<string, MetricTotals> {
  const byWeek = new Map<string, MetricTotals>();
  for (const cell of cells) {
    if (cell.scope !== "consolidated") continue;
    if (!isMetricKey(cell.metric_key)) continue; // a tenth engine metric is ignored, not fatal
    let totals = byWeek.get(cell.iso_week);
    if (totals === undefined) {
      // Zero-fill on first sight: a metric the fact store never wrote for a RECORDED week is
      // a real zero (nothing happened), which is a dash — not a hole, and not a gap.
      totals = zeroTotals();
      byWeek.set(cell.iso_week, totals);
    }
    // Assignment, not accumulation: the fact store holds one row per
    // (metric, scope, week) by fact_key, so a second cell is a re-read of the same fact.
    totals[cell.metric_key] = cell.value;
  }
  return byWeek;
}

/**
 * Phase 380 wave 3 task 3.2 (#1329) — the ISO weeks whose Loxo event harvest was known SHORT,
 * i.e. the weeks whose numbers are FLOORS rather than exact figures.
 *
 * <p>A week is short when ANY cell carrying its `iso_week` carries the engine's
 * `activity_harvest_incomplete` qualifier. Scope-agnostic on purpose:
 * `ReportingFactStore.BuildDimensions` stamps the qualifier onto EVERY fact row it writes for a
 * short week — the consolidated row and the per-consultant split alike — so "any cell of this
 * week" is the same answer the consolidated row alone would give, and it still tells the truth
 * if a week is ever read back split-only (the case {@link distinctWeeks} already keeps a row
 * for). The unsafe direction of error is the other one: presenting a floor as a final figure,
 * which is what `ReportingReadService.GetDimBool` documents on the read side.</p>
 *
 * <p><b>Per WEEK, not per page.</b> The PLAN's sketch for this task was
 * `cells.some(c => c.activity_harvest_incomplete)` — one verdict for the whole grid. That would
 * put the partial-data warning on EVERY row as soon as a single historical week was short, and
 * this page exists to show many weeks at once: AC-4's force-backfill is there precisely to
 * leave repaired weeks sitting beside unrepaired ones. A disclosure that fires on clean weeks
 * too is one a client learns to scroll past, so the verdict is scoped to the week whose cells
 * carry it.</p>
 *
 * <p>Absent reads as complete: the engine omits the key on every clean harvest and every fact
 * row written before this phase, so only `true` carries information (the rule
 * `api/types.ts` states for this field).</p>
 */
function incompleteWeeks(cells: readonly WeeklyActivityCell[]): Set<string> {
  const weeks = new Set<string>();
  for (const cell of cells) {
    if (cell.activity_harvest_incomplete && cell.iso_week) weeks.add(cell.iso_week);
  }
  return weeks;
}

/**
 * Every ISO week from the tenant's first observed week to its last, oldest → newest, with
 * the missing ones marked as gaps.
 *
 * <p>This is THE axis. {@link weekRows} reverses it for the table and {@link trendSeries}
 * reads it forwards for the charts, so a gap can never be a broken line in one place and a
 * missing row in the other.</p>
 */
function buildAxis(cells: readonly WeeklyActivityCell[]): AxisWeek[] {
  const observed = distinctWeeks(cells);
  if (observed.length === 0) return [];

  const totalsByWeek = consolidatedTotalsByWeek(cells);
  const shortWeeks = incompleteWeeks(cells);
  const first = parseIsoWeek(observed[0]!);
  const last = parseIsoWeek(observed[observed.length - 1]!);

  const labels = new Set<string>(observed);
  if (first !== null && last !== null) {
    // Walk Monday to Monday in UTC, filling in the weeks nobody recorded.
    const endMonday = isoWeekMondayUtc(last);
    // Bounded: ~20 years of weekly facts. A runaway loop here would hang the page, and a
    // tenant with more than that has a data problem this module cannot fix by looping.
    let monday = isoWeekMondayUtc(first);
    for (let step = 0; monday <= endMonday && step < 1040; step += 1) {
      labels.add(formatIsoWeek(isoWeekOfUtc(monday)));
      monday += WEEK_MS;
    }
  }

  return [...labels]
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .map((isoWeek) => {
      const totals = totalsByWeek.get(isoWeek);
      return {
        isoWeek,
        hasData: totals !== undefined,
        harvestIncomplete: shortWeeks.has(isoWeek),
        totals: totals ?? zeroTotals(),
      };
    });
}

// ── weekRows: the month-banded weekly table, newest first ───────────────────────────────

/** One row of the weekly table: a recorded week, or a labelled gap. */
export interface WeekRow {
  isoWeek: string;
  /** `yyyy-MM`, by the Thursday rule — the SAME month the read layer would give this week. */
  month: string;
  /** `January 2026` — the band heading. */
  monthLabel: string;
  /**
   * True when this row opens a new month band (the table is newest-first, so a band opens at
   * its LATEST week). The page renders a band heading before rows carrying this flag.
   */
  startsMonthBand: boolean;
  /** False = a GAP week: no consolidated facts were recorded between weeks that were. */
  hasData: boolean;
  /** {@link WEEK_GAP_LABEL} on a gap row, `null` on a recorded one. */
  gapLabel: string | null;
  /**
   * Phase 380 wave 3 task 3.2 (#1329) — true when THIS week's Loxo event harvest was known
   * short, so every number on the row is a FLOOR rather than a final figure and the page owes
   * the client a partial-data indicator (task 3.3). False is the ordinary case and renders
   * nothing.
   *
   * <p>Always a boolean, never `undefined`: the wire field is optional (absent = clean) but a
   * view model that made the page distinguish `false` from "not set" would earn a
   * `=== true` at every call site and hide a short week on the day someone wrote `!== false`.
   * The absent/false collapse happens once, here. See {@link incompleteWeeks} for why the
   * verdict is per week rather than per grid.</p>
   *
   * <p>camelCase, unlike the wire field it derives from (`activity_harvest_incomplete` on
   * `WeeklyActivityCell`): `WeekRow` is a portal view shape, not a mirror of a payload — its
   * siblings are `monthLabel` and `startsMonthBand`, and this reads with them.</p>
   */
  activityHarvestIncomplete: boolean;
  /** The nine cells in locked display order. EMPTY on a gap row — absence has no numbers. */
  values: MetricValue[];
}

/**
 * The weekly table model: consolidated cells grouped by ISO week, NEWEST FIRST, banded by
 * month, with absent weeks rendered as labelled gaps.
 *
 * <p>Newest-first because the page answers "how are we doing", and the current week is the
 * one being asked about; older weeks are context you scroll to. (The trend charts read the
 * other way — see {@link trendSeries} — which is why both come from one axis.)</p>
 *
 * <p>Each row also carries {@link WeekRow.activityHarvestIncomplete}, the harvest-integrity
 * verdict for THAT week ({@link incompleteWeeks}), so a week whose numbers are floors can say
 * so on its own row rather than the page saying it about every week at once.</p>
 */
export function weekRows(cells: readonly WeeklyActivityCell[]): WeekRow[] {
  const newestFirst = buildAxis(cells).reverse();
  let previousMonth: string | null = null;

  return newestFirst.map((week) => {
    const month = isoWeekToMonth(week.isoWeek);
    const startsMonthBand = month !== previousMonth;
    previousMonth = month;
    return {
      isoWeek: week.isoWeek,
      month,
      monthLabel: monthLabel(month),
      startsMonthBand,
      hasData: week.hasData,
      gapLabel: week.hasData ? null : WEEK_GAP_LABEL,
      activityHarvestIncomplete: week.harvestIncomplete,
      values: week.hasData ? metricValues(week.totals) : [],
    };
  });
}

// ── ytdTiles: the nine headline numbers ─────────────────────────────────────────────────

/** One YTD tile: a metric's year-to-date total, and how it reads. */
export interface YtdTile {
  key: MetricKey;
  /** The FULL label — a tile has room for "Forwarding Interviews". */
  label: string;
  total: number;
  /** {@link formatMetricValue} of `total`. */
  display: string;
  isZero: boolean;
}

/**
 * The nine YTD tiles in locked display order (Notes last): the sum of every CONSOLIDATED
 * cell whose ISO week falls in the ISO year of `now`.
 *
 * <p>Consolidated only. Summing the per-consultant cells as well would double every number
 * on the page — they are the same activity, split.</p>
 *
 * <p>`now` is a required parameter with no default, so the year boundary — the one moment
 * these tiles are hard to get right — is reachable from a test.</p>
 */
export function ytdTiles(cells: readonly WeeklyActivityCell[], now: Date): YtdTile[] {
  const year = currentIsoYear(now);
  const totals = zeroTotals();

  for (const cell of cells) {
    if (cell.scope !== "consolidated") continue;
    if (!isMetricKey(cell.metric_key)) continue;
    const parts = parseIsoWeek(cell.iso_week);
    if (parts === null || parts.year !== year) continue;
    totals[cell.metric_key] += cell.value;
  }

  return METRIC_TABLE.map((metric) => {
    const total = totals[metric.key];
    return {
      key: metric.key,
      label: metric.label,
      total,
      display: formatMetricValue(total),
      isZero: total === 0,
    };
  });
}

// ── trendSeries: nine small multiples ───────────────────────────────────────────────────

/**
 * One small multiple's data — shaped to drop straight into the design system's
 * `TrendSmallMultiple` props (`points` / `labels` / `yMax` / `ownScale`).
 */
export interface TrendSeries {
  key: MetricKey;
  /** The chart title: the FULL label, never the column abbreviation. */
  label: string;
  /** One entry per week, oldest → newest. `null` = a gap week: the line BREAKS there. */
  points: (number | null)[];
  /** The week labels, index-aligned with {@link points}. */
  labels: string[];
  /** The ceiling shared by the eight comparable metrics. Absent on the own-scale series. */
  yMax?: number;
  /** Present ONLY on {@link OWN_SCALE_METRIC} — the chart that is not comparable, saying so. */
  ownScale?: { label: string };
}

/**
 * The nine trend series over ALL weeks, oldest → newest.
 *
 * <p>Oldest-first even though the table is newest-first: a line chart is read left to right
 * as time passing, and reversing it would draw every trend backwards. Both orders come from
 * one axis ({@link buildAxis}), so the charts and the table always show the same weeks.</p>
 *
 * <p>Gap weeks are `null` points, which the chart primitive renders as a genuine break in
 * the line. Interpolating would draw a value nobody measured; plotting zero would report a
 * week of no activity. A recorded zero IS plotted, on the baseline.</p>
 *
 * <p><b>Scales.</b> The eight typed metrics share one ceiling so they can be compared at a
 * glance. Notes does not join them: it is the breadth counter, ≥ every typed bucket by
 * construction (`WeeklyReportChart.cs:61-63`), and on a shared scale it flattens the other
 * eight into a floor. It gets `ownScale`, which makes the primitive print its ceiling — an
 * own-scale chart without a printed ceiling is a chart that lies quietly. Never a dual
 * axis.</p>
 *
 * <p>The shared ceiling is never below 1. A zero ceiling would make the primitive divide by
 * zero, and a `NaN` coordinate is dropped silently by the browser — an invisible chart that
 * looks like missing data rather than a bug.</p>
 *
 * <p>A tenant with no facts at all gets NO series rather than nine empty ones: a trend over
 * zero periods is not a chart, and nine empty grid cells read as a broken page instead of as
 * the empty state the page shows for it.</p>
 */
export function trendSeries(cells: readonly WeeklyActivityCell[]): TrendSeries[] {
  const axis = buildAxis(cells);
  if (axis.length === 0) return [];
  const labels = axis.map((week) => week.isoWeek);

  const pointsFor = (key: MetricKey): (number | null)[] =>
    axis.map((week) => (week.hasData ? week.totals[key] : null));

  const shared = METRIC_TABLE.filter((metric) => metric.key !== OWN_SCALE_METRIC);
  let sharedObserved = 0;
  for (const metric of shared) {
    for (const week of axis) {
      if (!week.hasData) continue;
      const value = week.totals[metric.key];
      if (value > sharedObserved) sharedObserved = value;
    }
  }
  const sharedYMax = Math.max(1, sharedObserved);

  return METRIC_TABLE.map((metric) =>
    metric.key === OWN_SCALE_METRIC
      ? {
          key: metric.key,
          label: metric.label,
          points: pointsFor(metric.key),
          labels: [...labels],
          ownScale: { label: OWN_SCALE_LABEL },
        }
      : {
          key: metric.key,
          label: metric.label,
          points: pointsFor(metric.key),
          labels: [...labels],
          yMax: sharedYMax,
        },
  );
}

// ── weekSplit: one week, per consultant ─────────────────────────────────────────────────

/** One consultant's row inside an expanded week. */
export interface SplitRow {
  /** The source-system id — the row's key, and the join back to the roster. */
  loxoUserId: string;
  /** The live-resolved name, or `User {id}` when the roster cannot name this id. */
  name: string;
  /** True for a support consultant: the page renders the row dimmed. */
  dimmed: boolean;
  /** The nine cells in locked display order, zeros already dashed. */
  values: MetricValue[];
}

/**
 * The per-consultant split for ONE week — available for EVERY week in the grid, not just the
 * latest (that is the whole of AC-3: Alisha expands historical weeks).
 *
 * <p>Rows are the consultants the fact store actually recorded for that week, ordered by
 * `orderRoster` (`loxo_user_id` ASCENDING, numerically) — the shared primitive, so this
 * page, the charts and the Monday Teams card put the same person in the same place. A
 * consultant on the roster with NO cells that week gets NO row: inventing a row of zeros
 * would report a week of no activity for someone the report simply did not cover, which is
 * the same lie as filling a gap week with zeros.</p>
 *
 * <p>Names come from `displayName`, so an id the roster cannot name — a departed consultant
 * still present in historical weeks — reads as `User {id}` rather than as a bare number or a
 * blank. `is_support` comes from the roster payload, which reads the same tenant config the
 * forecast's attribution rule reads; the portal never hardcodes who is support.</p>
 *
 * @param cells The full grid. Only `per_consultant` cells for `isoWeek` are read.
 * @param roster The live roster; `null`/empty is legitimate (an unreachable roster degrades
 *   to an empty list) and every row then falls back to `User {id}`.
 * @param isoWeek The week to split, e.g. `2026-W08`.
 */
export function weekSplit(
  cells: readonly WeeklyActivityCell[],
  roster: readonly ReportingRosterConsultant[] | null | undefined,
  isoWeek: string,
): SplitRow[] {
  const rosterById = new Map<string, ReportingRosterConsultant>();
  for (const consultant of roster ?? []) rosterById.set(consultant.loxo_user_id, consultant);

  const byConsultant = new Map<string, MetricTotals>();
  for (const cell of cells) {
    if (cell.scope !== "per_consultant") continue;
    if (cell.iso_week !== isoWeek) continue;
    if (!isMetricKey(cell.metric_key)) continue;
    let totals = byConsultant.get(cell.loxo_user_id);
    if (totals === undefined) {
      totals = zeroTotals();
      byConsultant.set(cell.loxo_user_id, totals);
    }
    totals[cell.metric_key] = cell.value;
  }

  const rows = [...byConsultant.entries()].map(([loxoUserId, totals]) => {
    const consultant = rosterById.get(loxoUserId);
    return {
      loxoUserId,
      name: consultant?.name ?? null,
      isSupport: consultant?.is_support === true,
      totals,
    };
  });

  return orderRoster(rows).map((row) => ({
    loxoUserId: row.loxoUserId,
    name: displayName(row),
    dimmed: row.isSupport,
    values: metricValues(row.totals),
  }));
}
